import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import stateCityData from "../../../data/states.json";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEdit,
  FaMapMarkerAlt,
  FaUsers,
  FaCheck,
  FaTimes,
  FaGlobe,
  FaBuilding,
  FaHashtag,
  FaUserCheck,
  FaSpinner,
  FaArrowLeft,
  FaSave,
  FaFilter,
  FaSearch,
  FaTag,
  FaUser
} from "react-icons/fa";

const FreeAdsAdminModify = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    allStates: false,
    state: [],
    city: [],
    pincode: "",
    reportersIds: [],
    influencersIds: [],
    userType: "reporter"
  });

  const [cities, setCities] = useState([]);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const states = Object.keys(stateCityData);
  const [reporters, setReporters] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const location = useLocation();

  const [adId, setAdId] = useState(null);
  const [adData, setAdData] = useState(null);
  const [selectedReporters, setSelectedReporters] = useState([]);
  const [selectedInfluencers, setSelectedInfluencers] = useState([]);

  // Popup and loader states
  const [showPopup, setShowPopup] = useState(false);
  const [confirmModify, setConfirmModify] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);

  // 🔑 CRITICAL FIX: Add error and validation states
  const [apiError, setApiError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // 🔑 CRITICAL FIX: Add validation for location state
    if (!location.state) {
      toast.error("No advertisement data found. Redirecting to free ads list.");
      setTimeout(() => navigate("/admin/freependingads"), 2000);
      return;
    }

    if (!location.state.adId) {
      toast.error("Advertisement ID is missing. Redirecting to free ads list.");
      setTimeout(() => navigate("/admin/freependingads"), 2000);
      return;
    }

    if (!location.state.adData) {
      toast.error("Advertisement data is missing. Redirecting to free ads list.");
      setTimeout(() => navigate("/admin/freependingads"), 2000);
      return;
    }

    // Validate ad data structure
    const { adId, adData } = location.state;
    if (!adData._id || !adData.userType) {
      toast.error("Invalid advertisement data. Redirecting to free ads list.");
      setTimeout(() => navigate("/admin/freependingads"), 2000);
      return;
    }

    setAdId(adId);
    setAdData(adData);

    // Pre-populate form with existing ad data - but keep state/city unselected for fresh modification
    setForm({
      allStates: false, // Always start with allStates unselected
      state: [], // Always start with no states selected
      city: [], // Always start with no cities selected
      pincode: "",
      reportersIds: adData.reportersIds || [],
      influencersIds: adData.influencersIds || [],
      userType: adData.userType || "reporter"
    });

    setIsInitialized(true);
  }, [location, navigate]);

  // Fetch reporters and influencers from API with better error handling
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setApiError(null);

        // Validate token
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token not found. Please login again.");
          navigate("/login");
          return;
        }

        // Create axios instance with timeout and error handling
        const apiClient = axios.create({
          timeout: 10000,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Fetch reporters
        const reportersResponse = await apiClient.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/reporter`);

        if (!reportersResponse.data || !reportersResponse.data.reporters) {
          throw new Error("Invalid response format for reporters");
        }

        setReporters(reportersResponse.data.reporters);

        // Fetch influencers
        const influencersResponse = await apiClient.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/reporter?userType=influencer`);

        if (!influencersResponse.data || !influencersResponse.data.reporters) {
          throw new Error("Invalid response format for influencers");
        }

        setInfluencers(influencersResponse.data.reporters);

        console.log(`✅ Successfully fetched ${reportersResponse.data.reporters.length} reporters and ${influencersResponse.data.reporters.length} influencers`);

      } catch (error) {
        console.error("Failed to fetch users:", error);
        setApiError(error.message);

        if (error.response?.status === 401) {
          toast.error("Authentication failed. Please login again.");
          navigate("/login");
        } else if (error.response?.status === 403) {
          toast.error("Access denied. You don't have permission to view users.");
        } else if (error.code === 'ECONNABORTED') {
          toast.error("Request timeout. Please check your internet connection.");
        } else {
          toast.error("Failed to fetch users. Please try again later.");
        }

        setReporters([]);
        setInfluencers([]);
      } finally {
        setLoading(false);
      }
    };

    if (isInitialized) {
      fetchUsers();
    }
  }, [isInitialized, navigate]);

  // Update cities when state changes
  useEffect(() => {
    if (form.state.length > 0) {
      // Collect all cities from selected states
      let allCities = [];
      form.state.forEach((st) => {
        allCities = [...allCities, ...(stateCityData[st] || [])];
      });
      setCities([...new Set(allCities)]);
    } else {
      setCities([]);
    }
  }, [form.state]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "allStates") {
      setForm((prev) => ({
        ...prev,
        allStates: checked,
        state: [],
        city: [],
        pincode: "",
        reportersIds: [],
        influencersIds: [],
      }));
    } else if (name === "state") {
      setForm((prev) => {
        const newStates = checked
          ? [...prev.state, value]
          : prev.state.filter((s) => s !== value);
        return {
          ...prev,
          state: newStates,
          city: prev.city.filter((c) =>
            newStates.some((st) => (stateCityData[st] || []).includes(c))
          ), // Remove cities not in selected states
        };
      });
    } else if (name === "city") {
      setForm((prev) => ({
        ...prev,
        city: checked
          ? [...prev.city, value]
          : prev.city.filter((c) => c !== value),
      }));
    } else if (name === "userType") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        // Clear selections when userType changes
        reportersIds: value === "influencer" ? [] : prev.reportersIds,
        influencersIds: value === "reporter" ? [] : prev.influencersIds,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // 🔑 CRITICAL FIX: Add form validation
  const validateForm = useCallback(() => {
    const errors = {};

    // Validate user type
    if (!form.userType) {
      errors.userType = "Please select a user type";
    }

    // Validate targeting selection
    const hasSpecificUsers = form.reportersIds.length > 0 || form.influencersIds.length > 0;
    const hasLocationTargeting = form.allStates || form.state.length > 0 || form.city.length > 0 || form.pincode;

    if (!hasSpecificUsers && !hasLocationTargeting) {
      errors.targeting = "Please select either specific users or location targeting";
    }

    // Validate user selections based on userType
    if (form.userType === "reporter" && form.reportersIds.length === 0 && !hasLocationTargeting) {
      errors.reporters = "Please select at least one reporter or use location targeting";
    }

    if (form.userType === "influencer" && form.influencersIds.length === 0 && !hasLocationTargeting) {
      errors.influencers = "Please select at least one influencer or use location targeting";
    }

    if (form.userType === "both" && form.reportersIds.length === 0 && form.influencersIds.length === 0 && !hasLocationTargeting) {
      errors.users = "Please select at least one user or use location targeting";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous validation errors
    setValidationErrors({});

    // Validate form
    if (!validateForm()) {
      toast.error("Please fix the validation errors before submitting");
      return;
    }

    setShowPopup(true);
  };

  // 🔑 CRITICAL FIX: Optimize filtering with useMemo
  const filteredReporters = useMemo(() => {
    if (!reporters || reporters.length === 0) return [];

    const isAnyFilterApplied = form.allStates || form.state.length > 0 || form.city.length > 0 || form.pincode;

    if (!isAnyFilterApplied) {
      return reporters;
    }

    if (form.allStates) {
      return reporters;
    }

    return reporters.filter((user) => {
      if (form.pincode) {
        return String(user.pincode) === String(form.pincode);
      } else if (form.city.length > 0) {
        return form.city.includes(user.city) && form.state.includes(user.state);
      } else if (form.state.length > 0) {
        return form.state.includes(user.state);
      }
      return false;
    });
  }, [reporters, form.allStates, form.state, form.city, form.pincode]);

  const filteredInfluencers = useMemo(() => {
    if (!influencers || influencers.length === 0) return [];

    const isAnyFilterApplied = form.allStates || form.state.length > 0 || form.city.length > 0 || form.pincode;

    if (!isAnyFilterApplied) {
      return influencers;
    }

    if (form.allStates) {
      return influencers;
    }

    return influencers.filter((user) => {
      if (form.pincode) {
        return String(user.pincode) === String(form.pincode);
      } else if (form.city.length > 0) {
        return form.city.includes(user.city) && form.state.includes(user.state);
      } else if (form.state.length > 0) {
        return form.state.includes(user.state);
      }
      return false;
    });
  }, [influencers, form.allStates, form.state, form.city, form.pincode]);

  // Add handlers for user selection
  const handleReporterSelect = (id) => {
    setForm((prev) => {
      const updated = prev.reportersIds.includes(id)
        ? prev.reportersIds.filter((rid) => rid !== id)
        : [...prev.reportersIds, id];
      return {
        ...prev,
        reportersIds: updated,
      };
    });
  };

  const handleInfluencerSelect = (id) => {
    setForm((prev) => {
      const updated = prev.influencersIds.includes(id)
        ? prev.influencersIds.filter((iid) => iid !== id)
        : [...prev.influencersIds, id];
      return {
        ...prev,
        influencersIds: updated,
      };
    });
  };

  // Select all reporters
  const handleSelectAllReporters = () => {
    const availableReporterIds = filteredReporters
      .filter(reporter => {
        // Don't select reporters who are already in the ad's acceptedReporters
        const isAlreadySelected = adData?.acceptedReporters?.some(
          acceptedReporter => acceptedReporter.reporterId === reporter._id
        );
        return !isAlreadySelected;
      })
      .map(reporter => reporter._id);

    setForm((prev) => ({
      ...prev,
      reportersIds: [...new Set([...prev.reportersIds, ...availableReporterIds])],
    }));
  };

  // Unselect all reporters
  const handleUnselectAllReporters = () => {
    setForm((prev) => ({
      ...prev,
      reportersIds: [],
    }));
  };

  // Select all influencers
  const handleSelectAllInfluencers = () => {
    const availableInfluencerIds = filteredInfluencers
      .filter(influencer => {
        // Don't select influencers who are already in the ad's acceptedReporters
        const isAlreadySelected = adData?.acceptedReporters?.some(
          acceptedReporter => acceptedReporter.reporterId === influencer._id
        );
        return !isAlreadySelected;
      })
      .map(influencer => influencer._id);

    setForm((prev) => ({
      ...prev,
      influencersIds: [...new Set([...prev.influencersIds, ...availableInfluencerIds])],
    }));
  };

  // Unselect all influencers
  const handleUnselectAllInfluencers = () => {
    setForm((prev) => ({
      ...prev,
      influencersIds: [],
    }));
  };

  // Add refs for dropdowns
  const stateDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        stateDropdownRef.current &&
        !stateDropdownRef.current.contains(event.target)
      ) {
        setShowStateDropdown(false);
      }
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setShowCityDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 🔑 CRITICAL FIX: API call function with comprehensive error handling
  const modifyFreeAdAPI = async () => {
    setShowPopup(false);
    setTimeout(async () => {
      setShowApproveSpinner(true);
      setApiError(null);

      try {
        // Validate token
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        // Determine final userType based on selections
        let finalUserType = form.userType;
        if (form.reportersIds.length > 0 && form.influencersIds.length > 0) {
          finalUserType = "both";
        } else if (form.influencersIds.length > 0) {
          finalUserType = "influencer";
        } else if (form.reportersIds.length > 0) {
          finalUserType = "reporter";
        }

        const payload = {
          action: "modified",
          note: "Free ad modified with new targeting",
          state: form.state.length > 0 ? form.state[0] : "",
          cities: form.city.join(","),
          reportersIds: form.reportersIds.join(","),
          influencersIds: form.influencersIds.join(","),
          allStatesTrue: form.allStates,
          userType: finalUserType,
        };

        // Create axios instance with timeout
        const apiClient = axios.create({
          timeout: 15000,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        console.log("Sending modification request:", payload);

        const response = await apiClient.put(
          `${process.env.REACT_APP_API_BASE_URL}/admin/modify/free/ad/${adId}`,
          payload
        );

        if (!response.data || !response.data.success) {
          throw new Error(response.data?.message || "Invalid response from server");
        }

        console.log("Free ad modified successfully:", response.data);
        toast.success("Free ad is successfully modified!");

        setTimeout(() => {
          setShowApproveSpinner(false);
          navigate("/admin/freependingads");
        }, 1200);

      } catch (error) {
        setShowApproveSpinner(false);
        setApiError(error.message);

        console.error("Error modifying free ad:", error);

        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const message = error.response.data?.message || "Server error";

          switch (status) {
            case 400:
              toast.error(`Validation Error: ${message}`);
              break;
            case 401:
              toast.error("Authentication failed. Please login again.");
              setTimeout(() => navigate("/login"), 2000);
              break;
            case 403:
              toast.error("Access denied. You don't have permission to modify this ad.");
              break;
            case 404:
              toast.error("Advertisement not found.");
              break;
            case 500:
              toast.error("Internal Server Error. Please try again later.");
              break;
            default:
              toast.error(`Error ${status}: ${message}`);
          }
        } else if (error.request) {
          // Network error
          toast.error("Network error. Please check your internet connection.");
        } else if (error.code === 'ECONNABORTED') {
          // Timeout
          toast.error("Request timeout. Please try again.");
        } else {
          // Other error
          toast.error(`Error: ${error.message}`);
        }
      }
    }, 200);
  };

  // 🔑 CRITICAL FIX: Don't render until initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-4xl text-blue-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Loading Advertisement Data...</h2>
          <p className="text-gray-500 mt-2">Please wait while we prepare the modification form</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 pb-20">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Premium Header */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="bg-gradient-to-br from-indigo-600 via-blue-700 to-violet-800 rounded-[2.5rem] shadow-2xl p-6 sm:p-14 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl -mr-40 -mt-40" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-400/10 rounded-full blur-2xl -ml-32 -mb-32" />

          <div className="relative z-10">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-indigo-100 hover:text-white mb-8 group transition-all font-black text-xs uppercase tracking-widest"
            >
              <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" /> Back to campaigns
            </button>

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div className="flex items-center gap-4 sm:gap-8">
                <div className="p-4 sm:p-6 bg-white/15 rounded-[2rem] backdrop-blur-md shadow-inner border border-white/10">
                  <FaEdit className="text-2xl sm:text-5xl text-indigo-200" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-5xl font-black tracking-tight leading-none uppercase">Modify Campaign</h1>
                  <p className="text-indigo-100 mt-2 text-[10px] sm:text-sm font-black uppercase tracking-[0.2em] opacity-80">Refine Targeting • Re-deploy Assets</p>
                </div>
              </div>

              {adData && (
                <div className="bg-white/10 backdrop-blur-md p-6 rounded-[2rem] border border-white/10 shrink-0">
                  <p className="text-[10px] font-black text-indigo-100 uppercase tracking-widest mb-4 opacity-70">CURRENT INTEL</p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                    <div className="flex justify-between gap-4">
                      <span className="text-[10px] font-bold text-indigo-200 uppercase">ID</span>
                      <span className="text-xs font-black tracking-tight">{adData._id.slice(-8)}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-[10px] font-bold text-indigo-200 uppercase">Type</span>
                      <span className="text-xs font-black tracking-tight uppercase">{adData.userType}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-[10px] font-bold text-indigo-200 uppercase">Deployed</span>
                      <span className="text-xs font-black tracking-tight">{adData.acceptedReporters?.length || 0}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-[10px] font-bold text-indigo-200 uppercase">Target</span>
                      <span className="text-xs font-black tracking-tight">{adData.requiredReportersCount}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
        {/* Form Column */}
        <div className="lg:col-span-2 space-y-8 sm:space-y-12">
          {loading && (
            <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-xl flex items-center gap-4">
              <FaSpinner className="text-indigo-500 animate-spin text-2xl" />
              <p className="text-sm font-black text-slate-400 uppercase tracking-widest">Synchronizing operator data...</p>
            </div>
          )}

          <div className="bg-white rounded-[2.5rem] sm:rounded-[3rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
            <div className="p-8 sm:p-12 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-xl sm:text-2xl font-black text-slate-800 uppercase tracking-tight flex items-center gap-3">
                <FaFilter className="text-indigo-500" /> Optimization Parameters
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-8 sm:p-12 space-y-10 sm:space-y-12">
              {/* User Type */}
              <div className="space-y-6">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <FaUser className="text-indigo-400" /> Operational User Type
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {['reporter', 'influencer', 'both'].map((type) => (
                    <label
                      key={type}
                      className={`relative flex items-center justify-center p-5 rounded-2xl border-2 transition-all cursor-pointer group ${form.userType === type ? 'border-indigo-500 bg-indigo-50 shadow-lg shadow-indigo-100' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                    >
                      <input
                        type="radio" name="userType" value={type}
                        checked={form.userType === type} onChange={handleChange}
                        className="sr-only"
                      />
                      <span className={`text-xs font-black uppercase tracking-widest ${form.userType === type ? 'text-indigo-700' : 'text-slate-400 group-hover:text-slate-600'}`}>
                        {type === 'both' ? 'Hybrid (Both)' : `${type}s`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Geo-Targeting Section */}
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                    <FaMapMarkerAlt className="text-indigo-400" /> Geo-Targeting Protocols
                  </p>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <span className="text-[10px] font-black text-slate-500 uppercase">National Deployment</span>
                    <div className="relative">
                      <input
                        type="checkbox" name="allStates" checked={form.allStates}
                        onChange={handleChange} className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-emerald-500 transition-all after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                    </div>
                  </label>
                </div>

                {!form.allStates && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* State Dropdown */}
                    <div className="space-y-3" ref={stateDropdownRef}>
                      <span className="text-[10px] font-black text-slate-400 uppercase ml-2">Regional Selection</span>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowStateDropdown(!showStateDropdown)}
                          className="w-full p-4 sm:p-5 bg-slate-50 border-none rounded-2xl text-left text-xs font-bold text-slate-700 focus:ring-2 focus:ring-indigo-500 transition-all flex items-center justify-between"
                        >
                          {form.state.length > 0 ? `${form.state.length} States Locked` : 'Select Regions'}
                          <FaSearch className="text-slate-300" />
                        </button>
                        {showStateDropdown && (
                          <div className="absolute z-20 top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-3xl border border-slate-100 max-h-60 overflow-y-auto p-2 custom-scrollbar">
                            {states.map(s => (
                              <label key={s} className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors">
                                <input type="checkbox" name="state" value={s} checked={form.state.includes(s)} onChange={handleChange} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                                <span className="text-xs font-black text-slate-600 uppercase">{s}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* City Dropdown */}
                    <div className="space-y-3" ref={cityDropdownRef}>
                      <span className="text-[10px] font-black text-slate-400 uppercase ml-2">City-Level Targeting</span>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowCityDropdown(!showCityDropdown)}
                          disabled={form.state.length === 0}
                          className="w-full p-4 sm:p-5 bg-slate-50 border-none rounded-2xl text-left text-xs font-bold text-slate-700 focus:ring-2 focus:ring-indigo-500 transition-all flex items-center justify-between disabled:opacity-50"
                        >
                          {form.city.length > 0 ? `${form.city.length} Cities Locked` : 'Select Localities'}
                          <FaSearch className="text-slate-300" />
                        </button>
                        {showCityDropdown && (
                          <div className="absolute z-20 top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-3xl border border-slate-100 max-h-60 overflow-y-auto p-2 custom-scrollbar">
                            {cities.map(c => (
                              <label key={c} className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors">
                                <input type="checkbox" name="city" value={c} checked={form.city.includes(c)} onChange={handleChange} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                                <span className="text-xs font-black text-slate-600 uppercase">{c}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="pt-6 sm:pt-10">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white p-6 rounded-[2rem] font-black text-xs sm:text-sm uppercase tracking-[0.3em] shadow-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-4 group disabled:opacity-50"
                >
                  {loading ? <FaSpinner className="animate-spin" /> : <FaSave className="group-hover:scale-110 transition-transform" />}
                  {loading ? 'Processing Updates...' : 'Synchronize Modifications'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* User Selection Sidebar */}
        <div className="space-y-8">
          <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden sticky top-8">
            <div className="p-8 bg-slate-900 text-white">
              <h2 className="text-lg font-black uppercase tracking-tight flex items-center gap-3">
                <FaUsers className="text-indigo-400" /> Operator Roster
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">Personalized Deployment</p>
            </div>

            <div className="p-8 space-y-8">
              {/* Search Operators */}
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  placeholder="FILTER OPERATORS..."
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border-none rounded-xl text-[10px] font-black text-slate-700 focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Reporters Section */}
              {(form.userType === "reporter" || form.userType === "both") && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Reporters</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleSelectAllReporters} className="text-[8px] font-black text-indigo-600 uppercase hover:underline">All</button>
                      <button type="button" onClick={handleUnselectAllReporters} className="text-[8px] font-black text-rose-500 uppercase hover:underline">None</button>
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {filteredReporters.map((r, i) => {
                      const isPreserved = adData?.acceptedReporters?.some(ar => ar.reporterId === r._id);
                      return (
                        <label key={i} className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${isPreserved ? 'bg-emerald-50 opacity-60' : 'bg-slate-50 hover:bg-slate-100'}`}>
                          <input
                            type="checkbox" checked={form.reportersIds.includes(r._id) || isPreserved}
                            onChange={() => !isPreserved && handleReporterSelect(r._id)}
                            disabled={isPreserved}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="min-w-0">
                            <p className="text-[10px] font-black text-slate-700 uppercase truncate">{r.name}</p>
                            <p className="text-[8px] font-bold text-slate-400 truncate">{r.iinsafId || 'ID UNKNOWN'}</p>
                          </div>
                          {isPreserved && <FaUserCheck className="ml-auto text-emerald-500 text-xs" />}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Influencers Section */}
              {(form.userType === "influencer" || form.userType === "both") && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Influencers</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleSelectAllInfluencers} className="text-[8px] font-black text-indigo-600 uppercase hover:underline">All</button>
                      <button type="button" onClick={handleUnselectAllInfluencers} className="text-[8px] font-black text-rose-500 uppercase hover:underline">None</button>
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {filteredInfluencers.map((r, i) => {
                      const isPreserved = adData?.acceptedReporters?.some(ar => ar.reporterId === r._id);
                      return (
                        <label key={i} className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${isPreserved ? 'bg-emerald-50 opacity-60' : 'bg-slate-50 hover:bg-slate-100'}`}>
                          <input
                            type="checkbox" checked={form.influencersIds.includes(r._id) || isPreserved}
                            onChange={() => !isPreserved && handleInfluencerSelect(r._id)}
                            disabled={isPreserved}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="min-w-0">
                            <p className="text-[10px] font-black text-slate-700 uppercase truncate">{r.name}</p>
                            <p className="text-[8px] font-bold text-slate-400 truncate">{r.iinsafId || 'ID UNKNOWN'}</p>
                          </div>
                          {isPreserved && <FaUserCheck className="ml-auto text-emerald-500 text-xs" />}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeAdsAdminModify;
